import React from 'react';
import { X, Calendar, User, Package, Wallet } from 'lucide-react';
import { useEffect } from 'react';

export default function WalletModal({ isOpen, onClose, walletData = [], isDark = false }) {
   useEffect(() => {
      const handleEscape = (e) => {
         if (e.key === 'Escape' && isOpen) {
            onClose();
         }
      };

      if (isOpen) {
         document.addEventListener('keydown', handleEscape);
      }

      return () => {
         document.removeEventListener('keydown', handleEscape);
      };
   }, [isOpen, onClose]);
   const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
      });
   };

   const formatAmount = (amt) => {
      return new Intl.NumberFormat('en-IN', {
         style: 'currency',
         currency: 'INR',
      }).format(amt || 0);
   };

   const getTypeConfig = (type) => {
      const typeUpper = type?.toUpperCase();
      switch (typeUpper) {
         case 'CREDIT':
            return {
               bg: isDark ? 'from-green-900/20 to-emerald-900/20' : 'from-green-50 to-emerald-50',
               border: isDark ? 'border-green-700' : 'border-green-200',
               badge: 'bg-green-500',
               amount: isDark ? 'text-green-400' : 'text-green-600',
               iconColor: 'text-green-500',
            };
         case 'DEBIT':
            return {
               bg: isDark ? 'from-red-900/20 to-rose-900/20' : 'from-red-50 to-rose-50',
               border: isDark ? 'border-red-700' : 'border-red-200',
               badge: 'bg-red-500',
               amount: isDark ? 'text-red-400' : 'text-red-600',
               iconColor: 'text-red-500',
            };
         default:
            return {
               bg: isDark ? 'from-gray-800 to-slate-800' : 'from-gray-50 to-slate-50',
               border: isDark ? 'border-gray-700' : 'border-gray-200',
               badge: 'bg-gray-500',
               amount: isDark ? 'text-gray-400' : 'text-gray-600',
               iconColor: 'text-gray-500',
            };
      }
   };

   // Calculate total amount
   const totalAmount = walletData.reduce((sum, transaction) => {
      if (transaction.type?.toUpperCase() === 'CREDIT') {
         return sum + (transaction.amt || 0);
      } else if (transaction.type?.toUpperCase() === 'DEBIT') {
         return sum - (transaction.amt || 0);
      }
      return sum;
   }, 0);

   if (!isOpen) return null;

   return (
      <div
         className={`fixed inset-0 ${
            isDark ? 'bg-black/70' : 'bg-black/50'
         } flex items-center justify-center p-4 z-[1002] backdrop-blur-sm`}>
         <div
            className={`${
               isDark ? 'bg-slate-800' : 'bg-white'
            } rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col animate-scaleIn`}>
            {/* Header */}
            <div
               className={`flex items-center justify-between p-6 border-b ${
                  isDark ? 'border-slate-700' : 'border-gray-200'
               } flex-shrink-0`}>
               <div className="flex items-center gap-4">
                  <div
                     className={`w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg`}>
                     <Wallet className="w-6 h-6 text-white" />
                  </div>
                  <div>
                     <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        Wallet Transactions
                     </h2>
                  </div>
               </div>
               <button
                  onClick={onClose}
                  className={`${
                     isDark
                        ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  } transition-colors rounded-lg p-2`}>
                  <X size={24} />
               </button>
            </div>

            {/* Balance Summary */}
            <div
               className={`px-6 py-3 bg-gradient-to-r ${
                  isDark
                     ? 'from-green-900/30 to-emerald-900/30 border-slate-700'
                     : 'from-green-50 to-emerald-50 border-green-100'
               } border-b flex-shrink-0`}>
               <div className="flex items-center justify-between">
                  <div>
                     <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'} mb-1 font-medium`}>
                        Total Wallet Balance
                     </p>
                     <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                        {formatAmount(totalAmount)}
                     </p>
                  </div>
               </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
               {walletData.length > 0 ? (
                  <div className="space-y-4">
                     {walletData.map((transaction) => {
                        const config = getTypeConfig(transaction.type);

                        return (
                           <div
                              key={transaction.id}
                              className={`bg-gradient-to-r ${config.bg} border ${config.border} rounded-xl p-5 hover:shadow-lg transition-all duration-200 group`}>
                              <div className="flex items-start justify-between mb-3">
                                 <div className="flex items-center gap-3">
                                    <div
                                       className={`${config.badge} text-white px-3 py-1 rounded-full text-xs font-semibold uppercase`}>
                                       {transaction.type || 'N/A'}
                                    </div>
                                 </div>
                                 <div className="text-right">
                                    <div className={`text-2xl font-bold ${config.amount} flex items-center gap-1`}>
                                       {formatAmount(transaction.amt)}
                                    </div>
                                 </div>
                              </div>

                              {transaction.description && (
                                 <p
                                    className={`text-sm mb-4 leading-relaxed rounded-lg p-3 ${
                                       isDark ? 'text-slate-200 bg-slate-700/50' : 'text-gray-700 bg-white/50'
                                    }`}>
                                    {transaction.description}
                                 </p>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                 {transaction.createdAt && (
                                    <div
                                       className={`flex items-center gap-2 text-sm rounded-lg p-2 ${
                                          isDark ? 'text-slate-300 bg-slate-700/50' : 'text-gray-600 bg-white/50'
                                       }`}>
                                       <Calendar size={16} className={isDark ? 'text-slate-400' : 'text-gray-400'} />
                                       <span>{formatDate(transaction.createdAt)}</span>
                                    </div>
                                 )}

                                 {transaction.referralName && (
                                    <div
                                       className={`flex items-center gap-2 text-sm rounded-lg p-2 ${
                                          isDark ? 'text-slate-300 bg-slate-700/50' : 'text-gray-600 bg-white/50'
                                       }`}>
                                       <User size={16} className={isDark ? 'text-slate-400' : 'text-gray-400'} />
                                       <span>{transaction.referralName}</span>
                                    </div>
                                 )}

                                 {transaction.packageName && (
                                    <div
                                       className={`flex items-center gap-2 text-sm rounded-lg p-2 ${
                                          isDark ? 'text-slate-300 bg-slate-700/50' : 'text-gray-600 bg-white/50'
                                       }`}>
                                       <Package size={16} className={isDark ? 'text-slate-400' : 'text-gray-400'} />
                                       <span>{transaction.packageName}</span>
                                    </div>
                                 )}
                              </div>

                              {transaction.payNotes && (
                                 <div
                                    className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                                    <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                       Notes:
                                    </p>
                                    <p
                                       className={`text-sm rounded-lg p-2 ${
                                          isDark ? 'text-slate-200 bg-slate-700/50' : 'text-gray-700 bg-white/50'
                                       }`}>
                                       {transaction.payNotes}
                                    </p>
                                 </div>
                              )}
                           </div>
                        );
                     })}
                  </div>
               ) : (
                  <div className="text-center py-16">
                     <div
                        className={`w-20 h-20 ${
                           isDark ? 'bg-slate-700' : 'bg-gray-100'
                        } rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <Wallet className={`w-10 h-10 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                     </div>
                     <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                        No Transactions Yet
                     </h3>
                     <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                        Your wallet transaction history will appear here.
                     </p>
                  </div>
               )}
            </div>

            {/* Footer */}
            {walletData.length > 0 && (
               <div
                  className={`p-6 border-t rounded-b-2xl ${
                     isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'
                  } flex-shrink-0`}>
                  <div className="flex items-center justify-between">
                     <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                        Showing <span className="font-semibold">{walletData.length}</span> transaction
                        {walletData.length !== 1 ? 's' : ''}
                     </div>
                     <div className="flex items-center gap-3">
                        <button
                           onClick={onClose}
                           className={`px-6 py-2.5 rounded-lg hover:shadow-lg transition-all duration-200 font-medium shadow-md ${
                              isDark
                                 ? 'bg-slate-700 text-white hover:bg-slate-600'
                                 : 'bg-gray-800 text-white hover:bg-gray-700'
                           }`}>
                           Close
                        </button>
                     </div>
                  </div>
               </div>
            )}
         </div>

         {/* Animation styles */}
         <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
      </div>
   );
}
